import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';

import { MuiChip } from '@/components/chip/MuiChip';
import { HpUrlIcon } from '@/features/icon/HpUrlIcon';
import { MedicalCareIcon } from '@/features/icon/MedicalCareIcon';
import { PhoneIcon } from '@/features/icon/PhoneIcon';
import { CanRequestLabel } from '@/features/label/CanRequestLabel';
import { PartnershipsSummary } from '@/features/partnership/components/PartnershipsSummary';
import { FavoriteButton } from '@/features/station/FavoriteButton';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import {
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_responseFilter,
  ibow2_proto_RequestUser,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import { Dispatch, SetStateAction } from 'react';
import { RequestStatusChip } from '../RequestStatusChip';
import { useLogic } from './logic';

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalSearchFilters?: api_portalOfficeHospitalListing_responseFilter[];
  listingInfo: HospitalListingOutput;
  isPublicSearch: boolean;
  requestUser?: ibow2_proto_RequestUser;
  localFavorites: Array<ibow2_proto_RequestUserFavorite>;
  setLocalFavorites: Dispatch<
    SetStateAction<ibow2_proto_RequestUserFavorite[]>
  >;
  customLink?: string;
  mapMarkerPortalOfficeId?: number | null;
  isShowMap?: boolean;
};

export function HospitalListingItem({
  hospitalMedicalSubjects,
  hospitalSearchFilters,
  listingInfo,
  isPublicSearch,
  requestUser,
  localFavorites,
  setLocalFavorites,
  customLink,
  mapMarkerPortalOfficeId,
  isShowMap,
}: Props) {
  const { cardHref, thumbnailInfo, portalOfficeInfo, isCarelogood } = useLogic({
    hospitalMedicalSubjects,
    hospitalSearchFilters,
    listingInfo,
    isPublicSearch,
  });

  const portalOfficeId = listingInfo.portal_office_id;
  const isHovered = mapMarkerPortalOfficeId === portalOfficeId;

  return (
    <CardActionArea href={customLink || cardHref} target="_blank">
      <Box
        sx={{
          containerName: 'hospital-listing-container',
          containerType: 'inline-size',
        }}
      >
        <Card
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            boxShadow:
              isShowMap && isHovered
                ? `0px 0px 8px 0px rgba(62, 201, 190, 0.4), 0px 4px 16px 0px rgba(62, 201, 190, 0.2)`
                : `0px 0px 2px 0px rgba(0, 0, 0, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.10)`,
            borderRadius: 2,
            padding: 2,
            gap: {
              xs: 2,
              md: 3,
            },
            backgroundColor: 'white',
            position: 'relative',
            border: isShowMap && isHovered ? '2px solid #3ec9be' : 'none',
            '&:hover': {
              '& .highlight p': {
                textDecoration: 'underline',
                textDecorationColor: '#3ec9be',
                color: '#3ec9be',
              },
              '& .MuiCardMedia-img': {
                border: 'solid 2px #3ec9be',
              },
            },
            '@container hospital-listing-container (width < 600px)': {
              flexDirection: 'column',
              gap: 2,
            },
          }}
        >
          {!isPublicSearch && (
            <RequestStatusChip requestStatus={listingInfo.request_status} />
          )}

          <Box
            sx={{
              width: {
                xs: '100%',
                md: 320,
              },
              maxWidth: {
                xs: '100%',
                md: '40%',
              },
              aspectRatio: '16 / 9',
              height: {
                xs: 263,
                md: 220,
              },
              borderRadius: 1,
              margin: {
                xs: 'auto',
                md: '16px auto',
              },
              position: 'relative',
              overflow: 'hidden',
              '@container hospital-listing-container (width < 600px)': {
                width: '100%',
                maxWidth: '100%',
                aspectRatio: '16 / 9',
                height: 263,
                margin: 'auto',
              },
            }}
          >
            <img
              src={thumbnailInfo.image}
              alt={thumbnailInfo.alt}
              style={{
                borderRadius: 'inherit',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              py: 1,
              gap: 3,
              ':last-child': { pb: 1 },
              flex: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Box>
                    <MuiChip
                      label={`医科`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: '#FFAC8A',
                        height: '24px',
                        fontSize: '14px',
                        px: 0.5,
                      }}
                    />
                  </Box>

                  <div className="highlight">
                    <Typography
                      sx={{
                        verticalAlign: 'sub',
                        fontSize: '20px',
                        fontWeight: 700,
                      }}
                    >
                      {portalOfficeInfo.name}
                    </Typography>
                  </div>

                  {isCarelogood && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <CanRequestLabel />
                    </Box>
                  )}
                </Box>

                <Stack
                  direction="row"
                  justifyContent="end"
                  alignItems="center"
                  gap={1.5}
                >
                  {requestUser && !isPublicSearch && (
                    <FavoriteButton
                      requestUser={requestUser}
                      favorites={localFavorites}
                      setFavorites={setLocalFavorites}
                      portalOfficeId={listingInfo.portal_office.id}
                    />
                  )}
                </Stack>
              </Box>

              <PartnershipsSummary
                portalOfficeId={listingInfo.portal_office.id}
              />

              {portalOfficeInfo.appealPoints.length > 0 && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  spacing={1}
                  useFlexGap
                  sx={{
                    minHeight: 20,
                    gap: 0.5,
                  }}
                >
                  {portalOfficeInfo.appealPoints.map(
                    (appealPoint, appealPointIndex) => (
                      <MuiChip
                        key={appealPointIndex}
                        size="small"
                        sx={{
                          borderRadius: 6,
                          backgroundColor: '#FAFAFA',
                          color: '#595960',
                          fontWeight: 500,
                        }}
                        label={appealPoint.label}
                      />
                    ),
                  )}
                </Stack>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  color="#595960"
                  variant="JP/Body/m/Regular/Normal"
                  sx={{
                    order: {
                      xs: 1,
                      md: 2,
                    },
                    '@container hospital-listing-container (width < 600px)': {
                      order: 1,
                    },
                  }}
                >
                  {portalOfficeInfo.postCode
                    ? `${portalOfficeInfo.postCode} ${portalOfficeInfo.address}`
                    : `${portalOfficeInfo.address}`}
                </Typography>
              </Box>

              {portalOfficeInfo.tel && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PhoneIcon color="default" width={16} height={16} />
                  <Typography
                    color="#595960"
                    variant="JP/Body/m/Regular/Normal"
                    sx={{
                      order: {
                        xs: 1,
                        md: 2,
                      },
                      '@container hospital-listing-container (width < 600px)': {
                        order: 1,
                      },
                    }}
                  >
                    {portalOfficeInfo.tel}
                  </Typography>
                </Box>
              )}

              {portalOfficeInfo.medicalSubjects && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <MedicalCareIcon color="default" width={18} height={18} />
                  <Typography
                    color="#595960"
                    variant="JP/Body/m/Regular/Normal"
                    sx={{
                      order: {
                        xs: 1,
                        md: 2,
                      },
                      '@container hospital-listing-container (width < 600px)': {
                        order: 1,
                      },
                    }}
                  >
                    {portalOfficeInfo.medicalSubjects}
                  </Typography>
                </Box>
              )}

              {portalOfficeInfo.hpUrl && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <HpUrlIcon
                    color="default"
                    width={18}
                    height={18}
                    sxProps={{
                      marginTop: '2px',
                    }}
                  />
                  <Typography
                    color="#595960"
                    variant="JP/Body/m/Regular/Normal"
                    sx={{
                      order: {
                        xs: 1,
                        md: 2,
                        '@container hospital-listing-container (width < 600px)':
                          {
                            order: 1,
                          },
                      },
                    }}
                  >
                    {portalOfficeInfo.hpUrl}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  marginTop: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="JP/Body/s/Medium/Tight"
                  sx={{
                    fontSize: '14px',
                  }}
                  color="#C4C4C4"
                >
                  更新日
                </Typography>
                <Typography
                  variant="JP/Body/s/Medium/Tight"
                  sx={{
                    fontSize: '14px',
                  }}
                  color="#C4C4C4"
                >
                  {portalOfficeInfo.updatedAt}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </CardActionArea>
  );
}
